/**
 * Measures every GraphQL operation: duration, response size and how many rows came back.
 *
 * Implemented as a fetch wrapper rather than an ApolloLink on purpose. A link can see the
 * operation but not the raw response, which forces it to guess which Resource Timing entry
 * belongs to which call — and that guess is wrong as soon as requests overlap, silently
 * reporting one operation's size against another. Here the request and the response are in the
 * same function, so every number belongs to the call it is reported for.
 *
 * Inspect from the console:
 *   __propidaMetrics.summary()   grouped table, one row per operation
 *   __propidaMetrics.all()       every recorded call, including variables
 *   __propidaMetrics.windows()   the fromDate of each calendar fetch, to spot redundant refetches
 *   __propidaMetrics.clear()
 */

const MAX_RECORDS = 500;
const records = [];

/**
 * Our queries are mostly anonymous, so operationName is usually null. Falling back to the first
 * root field gives a usable label — "events", "visits", "recurrenceOverrides" — which is what
 * distinguishes the calls in practice.
 */
const labelFromQuery = (query) => {
  if (typeof query !== 'string') return 'unknown';

  // Anchored on the operation keyword rather than the first brace, because several documents
  // interpolate a fragment definition ahead of the operation and that fragment's first field
  // would otherwise be reported as the operation name.
  const keyed = /\b(?:query|mutation|subscription)\b[^{]*\{\s*([A-Za-z_][A-Za-z0-9_]*)/.exec(query);
  if (keyed) return keyed[1];

  // graphql-js prints an anonymous query with no variables in shorthand, dropping the `query`
  // keyword entirely — `{ activeUsers { id } }`. The spec only allows shorthand when it is the
  // document's sole definition, so anchoring at the start is unambiguous here.
  const shorthand = /^\s*\{\s*([A-Za-z_][A-Za-z0-9_]*)/.exec(query);
  return shorthand ? shorthand[1] : 'unknown';
};

const parseRequestBody = (body) => {
  if (typeof body !== 'string') return null;
  try {
    const parsed = JSON.parse(body);
    return {
      label: parsed.operationName || labelFromQuery(parsed.query),
      variables: parsed.variables,
    };
  } catch {
    return null;
  }
};

/** Row counts per top-level field, e.g. { events: 7587, visits: 412 }. */
const countRows = (data) => Object.fromEntries(
  Object.entries(data ?? {})
    .filter(([, value]) => Array.isArray(value))
    .map(([key, value]) => [key, value.length]),
);

/**
 * Wraps fetch so Apollo's HTTP layer is measured from the inside.
 * Pass to HttpLink as `fetch`.
 */
export const metricsFetch = async (input, init) => {
  const request = parseRequestBody(init?.body);
  const startedAt = performance.now();
  const response = await fetch(input, init);
  const ms = Math.round(performance.now() - startedAt);

  if (!request) return response;

  // Read from a clone so Apollo still gets an untouched body to consume.
  let text = '';
  try {
    text = await response.clone().text();
  } catch {
    return response;
  }

  let payload = null;
  try {
    payload = JSON.parse(text);
  } catch {
    payload = null;
  }

  records.push({
    operation: request.label,
    ms,
    // Exact decoded size of this response. Not an estimate.
    decodedBytes: new TextEncoder().encode(text).length,
    // What crossed the wire. For a compressed response this is the compressed size. Null when
    // the header is not readable, which is the case cross-origin unless the API lists
    // Content-Length in Access-Control-Expose-Headers.
    wireBytes: response.headers.get('content-length')
      ? Number(response.headers.get('content-length'))
      : null,
    encoding: response.headers.get('content-encoding') || null,
    rows: countRows(payload?.data),
    variables: request.variables,
    at: new Date().toISOString(),
  });
  if (records.length > MAX_RECORDS) records.shift();

  return response;
};

const kb = (bytes) => (bytes == null ? 'ej mätbar' : `${(bytes / 1024).toFixed(1)} kB`);

const median = (values) => [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)];

const summary = () => {
  const byOperation = new Map();
  records.forEach((r) => {
    const calls = byOperation.get(r.operation) ?? [];
    calls.push(r);
    byOperation.set(r.operation, calls);
  });

  const table = [...byOperation.entries()].map(([operation, calls]) => {
    const last = calls[calls.length - 1];
    return {
      operation,
      calls: calls.length,
      'ms (median)': median(calls.map((c) => c.ms)),
      'ms (max)': Math.max(...calls.map((c) => c.ms)),
      'wire (last)': kb(last.wireBytes),
      'decoded (last)': kb(last.decodedBytes),
      encoding: last.encoding ?? 'ingen',
      'rows (last)': JSON.stringify(last.rows),
    };
  }).sort((a, b) => b.calls - a.calls);

  // eslint-disable-next-line no-console
  console.table(table);
  return table;
};

/**
 * Which date windows the calendar actually fetched, in order. Repeats of the same fromDate mean
 * a window was refetched when the cache should have covered it.
 */
const windows = () => {
  const table = records
    .filter((r) => r.variables?.options?.fromDate)
    .map((r) => ({
      operation: r.operation,
      from: r.variables.options.fromDate.slice(0, 10),
      to: r.variables.options.toDate?.slice(0, 10),
      ms: r.ms,
      rows: JSON.stringify(r.rows),
    }));

  // eslint-disable-next-line no-console
  console.table(table);
  return table;
};

if (typeof window !== 'undefined') {
  // Dunder-prefixed on purpose, the same way __APOLLO_CLIENT__ is: a console handle, not app API.
  // eslint-disable-next-line no-underscore-dangle
  window.__propidaMetrics = {
    all: () => records,
    summary,
    windows,
    clear: () => { records.length = 0; },
  };
}
